import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ExternalLink, Video, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';

interface GoogleMeetTabProps {
  booking: {
    id: string;
    client_id: string;
    practitioner_id: string;
    start_time: string;
    end_time: string;
    status: string;
    format: string;
    clients?: {
      first_name: string;
      last_name: string;
    };
    practitioners?: {
      first_name: string;
      last_name: string;
    };
  };
  userRole?: 'client' | 'practitioner';
}

export function GoogleMeetTab({ booking, userRole = 'client' }: GoogleMeetTabProps) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [googleMeetConnected, setGoogleMeetConnected] = useState(false);
  const [isLoadingStatus, setIsLoadingStatus] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [meeting, setMeeting] = useState<any>(null);
  
  // Check if user has permission to create Google Meet (only practitioners)
  const canCreateMeeting = userRole === 'practitioner';
  const canJoinMeeting = true; // Both users can join

  const formatDateTime = (dateTime: string) => {
    return new Date(dateTime).toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getDuration = () => {
    const start = new Date(booking.start_time);
    const end = new Date(booking.end_time);
    const diffMs = end.getTime() - start.getTime();
    const diffMins = Math.round(diffMs / (1000 * 60));
    return `${diffMins} minutes`;
  };

  const clientName = booking.clients 
    ? `${booking.clients.first_name || ''} ${booking.clients.last_name || ''}`.trim() || 'Unknown Client'
    : 'Unknown Client';

  // Check Google Calendar connection status
  const checkGoogleStatus = async () => {
    try {
      // Get current session for authentication
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        console.error('No valid session for status check');
        setGoogleMeetConnected(false);
        setIsLoadingStatus(false);
        return;
      }

      const { data, error } = await supabase.functions.invoke('google-calendar-status', {
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });
      if (error) throw error;
      setGoogleMeetConnected(data?.connected || false);
    } catch (error: any) {
      console.error('Failed to check Google Calendar status:', error);
      
      // Check for reauth_required error
      if (error?.message?.includes('reauth_required') || error?.message?.includes('token expired')) {
        toast({
          variant: "destructive",
          title: "Google Calendar连接已过期",
          description: "需要重新连接Google Calendar才能创建会议链接"
        });
      }
      
      setGoogleMeetConnected(false);
    } finally {
      setIsLoadingStatus(false);
    }
  };

  // Manual refresh function for Google Calendar connection
  const refreshGoogleConnection = async () => {
    setIsLoadingStatus(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('No valid session');
      }

      // Call the refresh token function
      const { data, error } = await supabase.functions.invoke('google-calendar-refresh-token', {
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });

      if (error) throw error;

      if (data?.success) {
        toast({
          title: "连接已刷新",
          description: "Google Calendar连接已成功刷新"
        });
        // Recheck the status
        await checkGoogleStatus();
      } else if (data?.reauth_required) {
        toast({
          variant: "destructive",
          title: "需要重新认证",
          description: "请重新连接Google Calendar以继续使用"
        });
        // Redirect to settings for re-authentication
        navigate('/practitioner-dashboard?tab=settings');
      }
    } catch (error: any) {
      console.error('Failed to refresh Google Calendar connection:', error);
      
      if (error?.message?.includes('reauth_required') || error?.message?.includes('expired')) {
        toast({
          variant: "destructive",
          title: "需要重新认证",
          description: "Google Calendar连接已过期，请前往设置重新连接"
        });
        // Redirect to settings for re-authentication
        setTimeout(() => {
          navigate('/practitioner-dashboard?tab=settings');
        }, 2000);
      } else {
        toast({
          variant: "destructive",
          title: "刷新失败",
          description: error?.message || "无法刷新Google Calendar连接"
        });
      }
    } finally {
      setIsLoadingStatus(false);
    }
  };

  useEffect(() => {
    checkGoogleStatus();
    checkExistingMeeting();
  }, []);

  const checkExistingMeeting = async () => {
    try {
      const { data, error } = await supabase
        .from('google_calendar_meetings')
        .select('*')
        .eq('booking_id', booking.id)
        .maybeSingle();

      if (data && !error) {
        setMeeting({
          event_id: data.google_event_id,
          meeting_link: data.meeting_link,
          calendar_link: data.calendar_link,
          event_data: {
            summary: data.event_title,
            start: data.start_time,
            end: data.end_time
          }
        });
      }
    } catch (error) {
      console.log('No existing meeting found or error:', error);
    }
  };

  // Listen for OAuth callback messages
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      console.log('Received message:', event.data);
      if (event.origin !== window.location.origin) return;
      
      if (event.data.type === 'GOOGLE_CALENDAR_AUTH_SUCCESS') {
        toast({
          title: "Success",
          description: "Google Calendar connected successfully!",
        });
        setGoogleMeetConnected(true);
        // Refresh the connection status
        setTimeout(() => checkGoogleStatus(), 1000);
      } else if (event.data.type === 'GOOGLE_CALENDAR_AUTH_ERROR') {
        toast({
          title: "Authentication Failed",
          description: event.data.error || "Failed to connect Google Calendar",
          variant: "destructive",
        });
      }
    };

    // Enhanced message listener with retry logic and localStorage backup
    const handlePopupMessage = (event: MessageEvent) => {
      console.log('📨 Received popup message:', event.data, 'from origin:', event.origin);
      
      if (event.data?.type === 'GOOGLE_AUTH_SUCCESS') {
        console.log('✅ Google auth success message received');
        setGoogleMeetConnected(true);
        toast({
          title: "Connection Successful",
          description: "Google Calendar connected successfully!"
        });
      } else if (event.data?.type === 'GOOGLE_AUTH_ERROR') {
        console.log('❌ Google auth error message received:', event.data.message);
        toast({
          title: "Connection Failed", 
          description: event.data.message || "Failed to connect to Google Calendar",
          variant: "destructive"
        });
      }
    };

    // Add event listener for popup messages with enhanced error handling
    const messageListener = (event: MessageEvent) => {
      try {
        handlePopupMessage(event);
      } catch (error) {
        console.error('Error handling popup message:', error);
      }
    };
    
    window.addEventListener('message', messageListener, false);
    
    // Enhanced polling mechanism with better error handling
    const pollInterval = setInterval(() => {
      try {
        const authStatus = localStorage.getItem('google_calendar_auth_status');
        const authTimestamp = localStorage.getItem('google_calendar_auth_timestamp');
        
        if (authStatus && authTimestamp) {
          const timestamp = parseInt(authTimestamp);
          const now = Date.now();
          
          // Only process if the status update is recent (within last 30 seconds)
          if (now - timestamp < 30000) {
            if (authStatus === 'success') {
              console.log('✅ Auth success detected via localStorage polling');
              setGoogleMeetConnected(true);
              toast({
                title: "Connection Successful",
                description: "Google Calendar connected successfully!"
              });
              // Immediately check Google status to refresh state
              checkGoogleStatus();
            } else if (authStatus === 'error') {
              console.log('❌ Auth error detected via localStorage polling');
              toast({
                title: "Connection Failed",
                description: "Failed to connect to Google Calendar",
                variant: "destructive"
              });
            }
            
            // Clear the status after processing
            localStorage.removeItem('google_calendar_auth_status');
            localStorage.removeItem('google_calendar_auth_timestamp');
          }
        }
      } catch (error) {
        console.error('Error in localStorage polling:', error);
      }
    }, 1500); // Poll every 1.5 seconds for better responsiveness

    return () => {
      window.removeEventListener('message', messageListener, false);
      clearInterval(pollInterval);
    };
  }, [toast]);

  // Remove this function since we're redirecting to settings
  // Connection logic is now handled in the settings page

  const createMeeting = async () => {
    if (!googleMeetConnected) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Google Calendar is not connected. Please connect Google Calendar first."
      });
      return;
    }

    // Check if booking is in the past
    const startTime = new Date(booking.start_time);
    const now = new Date();
    if (startTime < now) {
      toast({
        variant: "destructive",
        title: "Cannot Create Meeting",
        description: "Cannot create meetings for past appointments. Meeting links can only be created for upcoming sessions."
      });
      return;
    }

    setIsCreating(true);
    try {
      // Ensure we have a valid session before making the request
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session?.access_token) {
        console.error('Session error:', sessionError);
        // Try to refresh session
        const { error: refreshError } = await supabase.auth.refreshSession();
        if (refreshError) {
          throw new Error('Authentication session expired. Please refresh the page and try again.');
        }
        // Get the refreshed session
        const { data: { session: newSession } } = await supabase.auth.getSession();
        if (!newSession?.access_token) {
          throw new Error('Failed to authenticate. Please refresh the page and try again.');
        }
      }

      console.log('✅ Session validated, calling Edge Function');

      const { data, error } = await supabase.functions.invoke('google-calendar-meeting', {
        body: { booking_id: booking.id },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });

      if (error) throw error;

      if (data && data.success) {
        setMeeting({
          event_id: data.event_id,
          meeting_link: data.meeting_link,
          calendar_link: data.calendar_link,
          has_virtual_support: data.has_virtual_support
        });
        toast({
          title: "Success",
          description: "Google Meet created successfully!"
        });
      } else {
        throw new Error("Invalid response from server");
      }
    } catch (error: any) {
      console.error('Failed to create Google Meet:', error);
      
      // Enhanced error handling for token expiration and reauth
      let errorTitle = "创建会议失败";
      let errorDescription = error?.message || "无法创建Google Meet会议";
      
      if (error?.message?.includes('reauth_required') || 
          error?.message?.includes('token expired') ||
          error?.message?.includes('token has expired')) {
        errorTitle = "Google Calendar连接已过期";
        errorDescription = "Token已过期，需要重新连接Google Calendar";
        
        // Show refresh button option
        toast({
          variant: "destructive",
          title: errorTitle,
          description: errorDescription
        });
        
        // Also update the connection status
        setGoogleMeetConnected(false);
        return;
      }
      
      // Check for 401 status specifically
      if (error?.status === 401 || error?.message?.includes('401') || error?.message?.includes('Unauthorized')) {
        errorTitle = "认证失败";
        errorDescription = "Google Calendar认证已过期，需要重新连接";
        
        toast({
          variant: "destructive",
          title: errorTitle,
          description: errorDescription
        });
        
        setGoogleMeetConnected(false);
        return;
      }
      
      toast({
        variant: "destructive",
        title: errorTitle,
        description: errorDescription
      });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6 pb-4 px-2 sm:px-0">
      <div className="text-center">
        <Video className="mx-auto h-10 w-10 sm:h-12 sm:w-12 text-muted-foreground mb-3 sm:mb-4" />
        <h2 className="text-lg sm:text-xl font-semibold mb-2">Google Meet Integration</h2>
        <p className="text-sm sm:text-base text-muted-foreground">
          Create and manage Google Meet for this session
        </p>
      </div>

      {/* Session Details */}
      <Card>
        <CardHeader className="pb-3 sm:pb-6">
          <CardTitle className="text-base sm:text-lg">Session Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 sm:space-y-3 p-3 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-0">
            <span className="font-medium text-sm sm:text-base">Client:</span>
            <span className="text-sm sm:text-base">{clientName}</span>
          </div>
          <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-0">
            <span className="font-medium text-sm sm:text-base">Date & Time:</span>
            <span className="text-sm sm:text-base">{formatDateTime(booking.start_time)}</span>
          </div>
          <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-0">
            <span className="font-medium text-sm sm:text-base">Duration:</span>
            <span className="text-sm sm:text-base">{getDuration()}</span>
          </div>
          <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-0">
            <span className="font-medium text-sm sm:text-base">Format:</span>
            <Badge variant={booking.format === 'virtual' ? 'default' : 'secondary'} className="self-start sm:self-auto">
              {booking.format}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Google Calendar Status - Only show for practitioners */}
      {canCreateMeeting && (
        <Card>
          <CardHeader className="pb-3 sm:pb-6">
            <CardTitle className="flex items-center justify-between text-base sm:text-lg">
              Google Calendar Status
              {isLoadingStatus && <Loader2 className="h-4 w-4 animate-spin" />}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 sm:p-6">
            <div className="flex items-center gap-3">
              <div className={`h-3 w-3 rounded-full ${googleMeetConnected ? 'bg-green-500' : 'bg-red-500'}`} />
              <span className="font-medium">
                {googleMeetConnected ? 'Connected' : 'Not Connected'}
              </span>
            </div>
            {!googleMeetConnected && (
              <div className="mt-3 space-y-2">
                <p className="text-sm text-muted-foreground mb-2">
                  Connect your Google Calendar to create meetings.
                </p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button
                    onClick={() => navigate('/practitioner-dashboard?tab=settings')}
                    variant="outline"
                    size="sm"
                    className="w-full sm:w-auto"
                  >
                    Go to Settings to Connect
                  </Button>
                  <Button
                    onClick={refreshGoogleConnection}
                    variant="ghost"
                    size="sm"
                    disabled={isLoadingStatus}
                    className="w-full sm:w-auto"
                  >
                    {isLoadingStatus && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Refresh Connection
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Meeting Actions */}
      <Card>
        <CardHeader className="pb-3 sm:pb-6">
          <CardTitle className="text-base sm:text-lg">Meeting Management</CardTitle>
          <CardDescription className="text-sm">
            Create and manage your Google Meet for this session
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!meeting ? (
            <div className="space-y-3">
              {new Date(booking.start_time) < new Date() && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-amber-800 text-sm">
                    This is a past appointment. Meeting links can only be created for upcoming sessions.
                  </p>
                </div>
              )}
              
              {!canCreateMeeting && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-blue-800 text-sm">
                    Your practitioner will create the Google Meet link for this session. You'll see the join button here once it's ready.
                  </p>
                </div>
              )}
              
              {canCreateMeeting && (
                <div className="flex gap-2">
                  <Button
                    onClick={createMeeting}
                    disabled={!googleMeetConnected || isCreating || new Date(booking.start_time) < new Date()}
                    className="flex-1"
                  >
                    {isCreating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Create Google Meet
                  </Button>
                  {googleMeetConnected && (
                    <Button
                      onClick={refreshGoogleConnection}
                      variant="outline"
                      size="sm"
                      disabled={isLoadingStatus}
                    >
                      {isLoadingStatus && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Refresh
                    </Button>
                  )}
                </div>
              )}
            </div>
          ) : (
              <div className="space-y-3">
                <div className="p-4 border rounded-lg bg-green-50 border-green-200">
                  <h4 className="font-medium text-green-800 mb-2">Meeting Available!</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-green-700">Event ID:</span>
                      <span className="font-mono text-xs break-all">{meeting.event_id}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-green-700">Session:</span>
                      <span className="text-xs">{formatDateTime(booking.start_time)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-green-700">Meet Link:</span>
                      {meeting.meeting_link ? (
                        <a 
                          href={meeting.meeting_link} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 text-xs underline"
                        >
                          Google Meet Link
                        </a>
                      ) : (
                        <span className="text-amber-600 text-xs">Not Available</span>
                      )}
                    </div>
                    {!meeting.meeting_link && meeting.has_virtual_support === false && (
                      <div className="text-amber-600 text-xs">
                        Note: Service doesn't support virtual format, calendar event created without meeting link.
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="flex gap-2">
                  {meeting.meeting_link ? (
                    <Button
                      onClick={() => window.open(meeting.meeting_link, '_blank')}
                      className="flex-1"
                    >
                      <ExternalLink className="mr-2 h-4 w-4" />
                      Join Google Meet
                    </Button>
                  ) : (
                    <Button
                      className="flex-1"
                      disabled
                      variant="outline"
                    >
                      <ExternalLink className="mr-2 h-4 w-4" />
                      No Meeting Link
                    </Button>
                  )}
                  {canCreateMeeting && (
                    <Button
                      onClick={() => window.open(meeting.calendar_link || `https://calendar.google.com/calendar/event?eid=${encodeURIComponent(meeting.google_event_id || meeting.event_id)}`, '_blank')}
                      variant="outline"
                      className="flex-1"
                    >
                      <Video className="mr-2 h-4 w-4" />
                      View Calendar
                    </Button>
                  )}
                </div>
              </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}